import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import "./UploadReport.css";
import { notification, Spin, Card, Input, Button, Progress, Divider, Tag } from "antd";
import moment from "moment";
import {
  HiOutlineChevronLeft,
  HiOutlineCloudUpload,
  HiOutlineDocumentText,
  HiOutlineCheckCircle,
  HiOutlineCalendar
} from 'react-icons/hi';
import Cookies from "js-cookie";
import { encryptData, decryptData } from '../Crypto_Helpers/cryptoHelper';
import { initEncryptionSession } from '../../encryptionSession';

// Icons
import MaleIcon from "../../assets/Doctor_Images/male_user.png";
import FemaleIcon from "../../assets/Doctor_Images/female_user.png";

const UploadReport = () => {
  const history = useHistory();
  const { patientId, bookingId, date, frompage } = useParams();

  // State
  const [loading, setLoading] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [reportName, setReportName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("No file selected");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAppointmentDetails();
  }, [patientId, bookingId, date]);

  const fetchAppointmentDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("clinic/getAppointmentList", {
        clinic_id: Cookies.get("clinic_id"),
        doctor_id: Cookies.get("doctor_id"),
        patient_type: "",
        period: "day",
        search_date: date,
        search_date_to: date,
      });

      if (response.data.data && response.data.data[0]) {
        const aesKey = await initEncryptionSession();
        const decryptedData = await decryptData(response.data.data[0].result, aesKey);
        const filtered = decryptedData.find(x => x.patient_id == patientId && x.booking_id == bookingId);
        if (filtered) setAppointment(filtered);
      }
    } catch (err) {
      console.error("Failed to fetch appointment", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("module_id", "3");
    formData.append("file", file);
    formData.append("pathLocation", "CLINICREPORT/");

    try {
      const response = await axiosInstance({
        method: "POST",
        url: "doctor/awsS3FileUpload",
        data: formData,
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentage);
        },
      });

      let resObj = response.data.encryptedData || response.data.data;
      if (typeof resObj === "string") {
        const aesKey = await initEncryptionSession();
        resObj = await decryptData(resObj, aesKey);
      }
      const uploadedUrl = resObj?.privatePathUrl || resObj?.url || (typeof resObj === "string" ? resObj : "");
      if (uploadedUrl) {
        setFileUrl(uploadedUrl);
        notification.success({ message: "File uploaded to server successfully" });
      } else {
        notification.error({ message: "Upload failed: Could not get file URL" });
      }
    } catch (err) {
      console.error("Upload error", err);
      notification.error({ message: "Failed to upload file to storage" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!reportName.trim()) {
      notification.error({ message: "Please enter a report name" });
      return;
    }
    if (!fileUrl) {
      notification.error({ message: "Please select and upload a file first" });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post("doctor/reportByClinic", {
        patientId: patientId,
        doctorId: Cookies.get("Doctor_id") || Cookies.get("doctor_id") || appointment?.doctor_id,
        bookingId: bookingId,
        clinicId: Cookies.get("Clinic_id") || Cookies.get("clinic_id") || appointment?.clinic_id,
        reportName: reportName,
        reportFile: fileUrl.split("?")[0],
        dateValue: moment().format("YYYY-MM-DD"),
        is_walkin: 0,
      });

      if (response.data.status === 1 || response.data.status === 200) {
        notification.success({ message: "Report successfully linked to patient record" });
        setTimeout(() => {
          history.push(`/dashboard/appointments/report/${patientId}/${bookingId}/${date}`);
        }, 1000);
      } else {
        notification.error({ message: response.data.msg || "Failed to save report" });
      }
    } catch (err) {
      console.error("Submit error", err);
      notification.error({ message: "An error occurred while saving the report" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !appointment) {
    return <div className="h-screen w-full flex items-center justify-center bg-teal-50"><Spin size="large" /></div>;
  }

  return (
    <div className="upload_report_page min-h-screen bg-slate-50/50 animate-in fade-in duration-700">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-30 px-8 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-6 max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => history.goBack()} className="p-2.5 hover:bg-teal-50 rounded-2xl text-teal-600 transition-all active:scale-95">
              <HiOutlineChevronLeft size={24} />
            </button>
            <div>
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">Upload Lab Report</h2>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-0.5">Add diagnostic documents to patient file</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-8 space-y-8">
        {/* Patient Profile Header */}
        {appointment && (
          <div className="bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-slate-50 shadow-sm">
                <img src={appointment.gender.toLowerCase().startsWith('m') ? MaleIcon : FemaleIcon} alt="patient" />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-800 leading-none">{appointment.name}</h3>
                <div className="flex items-center gap-3 mt-2">
                  <Tag className="rounded-lg bg-teal-50 text-teal-600 border-teal-100 font-bold px-3">ID000{appointment.patient_id}</Tag>
                  <span className="text-slate-400 text-xs font-bold uppercase tracking-widest">{appointment.age} Yrs • {appointment.gender}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Appointment Date</p>
              <div className="flex items-center gap-2 text-slate-700 font-bold">
                <HiOutlineCalendar className="text-teal-500" />
                <span>{moment(appointment.appointment_date).format("DD MMM YYYY")}</span>
              </div>
            </div>
          </div>
        )}

        {/* Upload Form */}
        <Card className="rounded-[40px] border-none shadow-xl overflow-hidden p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Form Inputs */}
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider mb-3 ml-1">Report Name</label>
                <Input
                  placeholder="e.g. Blood Test, X-Ray Chest"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  className="h-14 rounded-2xl border-slate-200 focus:border-teal-400 text-base font-medium transition-all"
                />
                <p className="text-[10px] text-slate-400 mt-2 ml-1 font-bold">Give a clear name to identify this report later.</p>
              </div>

              <div>
                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider mb-3 ml-1">Select Document</label>
                <div className="relative group">
                  <div className="h-40 w-full border-2 border-dashed border-slate-200 group-hover:border-teal-400 transition-all rounded-[32px] flex flex-col items-center justify-center gap-3 bg-slate-50 group-hover:bg-teal-50/30 overflow-hidden relative">
                    <HiOutlineCloudUpload size={40} className="text-slate-300 group-hover:text-teal-500 transition-all" />
                    <p className="text-sm font-bold text-slate-400 group-hover:text-teal-600">{fileName}</p>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </div>
                  {isUploading && (
                    <div className="mt-4 px-2">
                      <Progress
                        percent={uploadProgress}
                        status="active"
                        strokeColor={{ '0%': '#10b981', '100%': '#34d399' }}
                      />
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-3 ml-1 font-bold">Accepted formats: PDF, JPG, PNG (Max 5MB)</p>
              </div>

              <Divider className="my-10" />

              <div className="flex gap-4">
                <Button
                  size="large"
                  className="flex-1 h-14 rounded-2xl font-black text-slate-500 border-slate-200 hover:text-slate-800 transition-all"
                  onClick={() => history.goBack()}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  disabled={!fileUrl || isUploading}
                  loading={isSubmitting}
                  className="flex-[2] h-14 rounded-2xl bg-teal-600 hover:!bg-teal-700 font-black border-none shadow-xl shadow-teal-100 disabled:opacity-50"
                  onClick={handleSubmit}
                >
                  {isSubmitting ? "Processing..." : "Finish & Save Report"}
                </Button>
              </div>
            </div>

            {/* Preview Area */}
            <div className="bg-slate-50/50 rounded-[32px] border border-slate-100 flex flex-col items-center justify-center p-6 min-h-[400px]">
              {!fileUrl ? (
                <div className="text-center opacity-40">
                  <HiOutlineDocumentText size={64} className="mx-auto mb-4 text-slate-300" />
                  <p className="font-bold text-slate-400">File Preview will appear here</p>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] font-black text-teal-600 uppercase tracking-widest">Preview Panel</span>
                    <button onClick={() => setFileUrl("")} className="text-rose-500 hover:text-rose-600 text-[10px] font-bold uppercase">Remove File</button>
                  </div>
                  <div className="flex-1 rounded-2xl overflow-hidden shadow-sm bg-white border border-slate-200 relative">
                    {fileUrl.toLowerCase().endsWith('.pdf') || fileUrl.includes('.pdf?') ? (
                      <object
                        data={fileUrl}
                        type="application/pdf"
                        className="w-full h-full min-h-[350px]"
                      >
                        <div className="p-10 text-center">
                          <p className="text-sm font-bold">PDF Preview ready</p>
                        </div>
                      </object>
                    ) : (
                      <img
                        src={fileUrl}
                        alt="Uploaded report preview"
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UploadReport;
